import { CPENZLoader } from "@/components/CPENZLoader";
import { Lc } from "@/components/ideaos/shared";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Lock, Sparkles, Loader2, Lightbulb, ChevronDown, Pencil } from "lucide-react";
import Navbar from "@/components/Navbar";
import { CPENZIcon } from "@/components/CPENZIcon";
import { MarketTeaser } from "@/components/MarketTeaser";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { IdeaPreview } from "@/components/IdeaPreview";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useAuthDialog } from "@/context/AuthDialogContext";
import api, { formatApiError } from "@/lib/api";
import { streamStage, partialStage } from "@/lib/stageStream";
import { STEPS, FREE_STAGES } from "@/data/steps";
import { EXAMPLES } from "@/data/examples";
import { AnswerQuality } from "@/components/AnswerQuality";
import { toast } from "sonner";

export default function IdeaFlow() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user, ready } = useAuth();
  const authDialog = useAuthDialog();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("answering"); // answering | reviewing | upsell | finalizing


  const unlocked = project?.unlocked || (user && ["START", "PRO", "ELITE"].includes(user.plan));

  const initProject = useCallback(async () => {
    if (id) {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      const answered = Object.keys(data.answers || {});
      if (answered.length === 0 && data.capture_text && data.snapshot) {
        setAnswer(data.capture_text);
        if (location.state?.skipPreview) {
          setIdx(1);
          setAnswer("");
          setPhase("answering");
        } else {
          setIdx(0);
          setPhase("preview");
        }
        submitStage(data, data.capture_text, true);
        return;
      }
      if (answered.length === 0 && data.capture_text) setAnswer(data.capture_text);
      const nextIdx = STEPS.findIndex((s) => !answered.includes(s.key));
      const startIdx = nextIdx === -1 ? STEPS.length : nextIdx;
      setIdx(startIdx);
      setPhase("answering");
    } else {
      const seed = (location.state?.seedIdea || "").trim();
      if (seed.length >= 8) {
        const { data } = await api.post("/ideas/capture", { text: seed, lang });
        navigate(`/app/idea/${data.id}`, { replace: true });
        return;
      }
      const { data } = await api.post("/projects", { lang });
      setProject(data);
      setIdx(0);
      setAnswer(seed);
      // move URL to project id (replace) so refresh works
      navigate(`/app/idea/${data.id}`, { replace: true, state: { keep: true } });
    }
    // eslint-disable-next-line
  }, [id, lang, location.state, navigate]);

  useEffect(() => {
    if (!ready) return;
    if (user === false) {
      navigate("/", { replace: true });
      authDialog.openAuth(() => navigate("/app/new"));
      return;
    }
    if (user) initProject();
    // eslint-disable-next-line
  }, [ready, user, id]);

  useEffect(() => {
    if (!project || phase === "preview") return;
    // determine phase for current idx
    if (idx >= STEPS.length) {
      setPhase(unlocked ? "final" : "upsell");
      return;
    }
    if (idx >= FREE_STAGES && !unlocked) {
      setPhase("upsell");
      return;
    }
    const key = STEPS[idx].key;
    if (project.stage_analysis?.[key]) {
      setResult(project.stage_analysis[key]);
      setAnswer(project.answers?.[key] || "");
      setPhase("reviewing");
    } else if (phase !== "answering") {
      setPhase("answering");
    }
    // eslint-disable-next-line
  }, [idx, project, unlocked]);

  const submitStage = async (proj, text, silent = false) => {
    const step = STEPS[0];
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${proj.id}/stage`, { step_key: step.key, question: step.q[lang], answer: text, lang });
      setResult(data.result);
      const p = await api.get(`/projects/${proj.id}`);
      setProject(p.data);
      if (!silent) toast.success(data.result?.momentum || t("toast.stage"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setPhase("answering");
    } finally {
      setLoading(false);
    }
  };

  const [live, setLive] = useState(null);

  const submit = async () => {
    if (!answer.trim() || !project) return;
    const step = STEPS[idx];
    setLoading(true);
    setLive({ title: null, summary: null, points: [], momentum: null });
    try {
      const data = await streamStage(project.id, { step_key: step.key, question: step.q[lang], answer, lang }, (text) => setLive(partialStage(text)));
      setLive(null);
      setResult(data.result);
      setPhase("reviewing");
      const p = await api.get(`/projects/${project.id}`);
      setProject(p.data);
      toast.success(data.result?.momentum || t("toast.stage"));
    } catch (e) {
      setLive(null);
      const status = e.status || e.response?.status;
      if (status === 402) setPhase("upsell");
      else toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const inputRef = useRef(null);
  const tailRef = useRef(null);
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [idx]);
  useEffect(() => { if (phase === "answering") setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 80); }, [idx, phase]);
  // Follow cpenz's answer as it streams so the "continue" button is already in view when it lands
  useEffect(() => {
    if (!(live && loading) && !(phase === "reviewing" && result)) return;
    const id = setTimeout(() => {
      const el = tailRef.current || document.querySelector('[data-testid="flow-continue-btn"]') || document.querySelector('[data-testid="flow-result"]'); if (!el) return;
      const target = el.getBoundingClientRect().bottom + window.scrollY - window.innerHeight + 140;
      if (target > window.scrollY + 8) window.scrollTo({ top: target, behavior: "smooth" });
    }, 260);
    return () => clearTimeout(id);
  }, [live, result, phase, loading]);
  // Enter continues in review mode (desktop and mobile); typing Enter in the field submits on desktop
  useEffect(() => {
    if (phase !== "reviewing" || !result) return;
    const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey && !(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLInputElement)) { e.preventDefault(); goNext(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [phase, result]); // eslint-disable-line react-hooks/exhaustive-deps
  const onAnswerKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(min-width: 768px)").matches && answer.trim() && !loading) { e.preventDefault(); submit(); }
  };
  const editAnswer = () => { setResult(null); setPhase("answering"); setTimeout(() => inputRef.current?.focus(), 80); };

  const goNext = () => {
    setResult(null);
    setAnswer("");
    setIdx((i) => i + 1);
  };
  const goBack = () => {
    if (idx === 0) return;
    setResult(null);
    setIdx((i) => i - 1);
  };

  const startCheckout = () => {
    if (!user) { authDialog.openAuth(() => navigate(`/app/checkout/${project.id}`)); return; }
    navigate(`/app/checkout/${project.id}`);
  };

  const finalize = async () => {
    setPhase("finalizing");
    try {
      const { data } = await api.post(`/projects/${project.id}/finalize`, {});
      setProject(data);
      navigate(`/app/home?welcome=create&project=${project.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && detail === "archive_full") { toast.error(t("checkout.archive_full")); setPhase("final"); navigate("/app/profile"); }
      else if (e.response?.status === 402) setPhase("upsell");
      else { toast.error(formatApiError(detail) || e.message); setPhase("final"); }
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center"><CPENZLoader size={96} /></div>
    );
  }

  const totalRail = [
    ...STEPS.map((s) => ({ num: s.num, label: s.label[lang], key: s.key })),
    { num: "12", label: lang === "IT" ? "Punteggio" : "Score", key: "_score" },
    { num: "13", label: lang === "IT" ? "Prossima mossa" : "Next move", key: "_next" },
  ];
  const answeredCount = Object.keys(project.answers || {}).length;

  return (
    <div className="min-h-screen bg-white">
      <Navbar onStart={() => navigate("/app/new")} onLogin={() => authDialog.openAuth(() => navigate("/app/dashboard"))} />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-6 md:pt-8 pb-44 grid lg:grid-cols-[240px_1fr] gap-10">
        {/* RAIL */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-1" data-testid="flow-rail">
            {totalRail.map((s, i) => {
              const done = i < answeredCount || (i >= STEPS.length && project.score);
              const active = i === idx || (phase === "final" && i >= STEPS.length) || (phase === "upsell" && i === Math.max(idx, FREE_STAGES));
              return (
                <div key={s.key} data-testid={`flow-rail-${s.key}`} className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm ${active ? "bg-[#f4f6fa] font-semibold text-[#0b0f1a]" : s.key === "_score" ? "text-[#d97706]" : s.key === "_next" ? "text-[#10b981]" : "text-slate-400"}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] shrink-0 ${done ? (s.key === "_score" ? "bg-[#d97706]" : s.key === "_next" ? "bg-[#10b981]" : "bg-[#7c3aed]") + " text-white" : active ? "border border-[#7c3aed] text-[#7c3aed]" : s.key === "_score" ? "border border-[#d97706]/60 text-[#d97706]" : s.key === "_next" ? "border border-[#10b981]/60 text-[#10b981]" : "border border-slate-200"}`}>
                    {done ? <Check size={12} /> : s.num}
                  </span>
                  <span className="leading-tight">{s.label}</span>
                  {i >= FREE_STAGES && !unlocked && <Lock size={12} className="ml-auto text-slate-300" />}
                </div>
              );
            })}
          </div>
        </aside>

        {/* MAIN */}
        <main>
          {/* progress: "Domanda X di 11 · N%" — one question per screen */}
          {(phase === "answering" || phase === "reviewing") && idx < STEPS.length && (
            <div className="mb-6 w-full max-w-2xl mx-auto" data-testid="flow-progress">
              <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-[#86868b]">
                <span data-testid="flow-progress-label">{lang === "IT" ? `Domanda ${idx + 1} di ${STEPS.length}` : `Question ${idx + 1} of ${STEPS.length}`}</span>
                <span className="tabular-nums" data-testid="flow-progress-pct">{Math.round((idx / STEPS.length) * 100)}%</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-[#e8e8ed] overflow-hidden">
                <div className="h-full bg-[#7c3aed] transition-all duration-300 ease-out" style={{ width: `${(idx / STEPS.length) * 100}%` }} />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {phase === "preview" && project.snapshot && (
              <IdeaPreview key="preview" name={project.name} snapshot={project.snapshot} loading={loading} onNext={() => { setPhase("answering"); goNext(); }} />
            )}
            {(phase === "answering" || phase === "reviewing") && idx < STEPS.length && (
              <motion.div key={`step-${idx}-${phase}`} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="w-full max-w-2xl mx-auto lg:text-center" data-testid="flow-step">
                <div className="flex items-center gap-3 flex-wrap lg:justify-center">
                  {idx < FREE_STAGES && <span className="cpenz-overline">{t("flow.free_badge")}</span>}
                  <span className="text-xs font-semibold text-slate-400">{t("flow.step_of", { n: STEPS[idx].num })}</span>
                </div>
                <h1 className="mt-3 cpenz-display text-3xl sm:text-4xl text-[#0b0f1a]" data-testid="flow-question">{STEPS[idx].q[lang]}</h1>
                {idx < 3 && phase === "answering" && (
                  <div className="mt-4 rounded-2xl border border-[#7c3aed]/20 bg-[#7c3aed]/5 px-4 py-3 text-left flex items-start gap-2.5" data-testid="flow-precision-hint">
                    <Sparkles size={15} className="text-[#7c3aed] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#0b0f1a] leading-relaxed"><span className="font-semibold">{t("flow.precision_title")}</span> {t("flow.precision_body")}</p>
                  </div>
                )}

                <Textarea
                  ref={inputRef}
                  onKeyDown={onAnswerKey}
                  autoFocus
                  data-testid="flow-answer-input"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder={STEPS[idx].ph[lang]}
                  rows={5}
                  disabled={phase === "reviewing"}
                  className="mt-6 rounded-2xl border-slate-200 text-lg leading-relaxed p-5 focus-visible:ring-[#7c3aed]"
                />
                {phase === "answering" && <AnswerQuality text={answer} />}
                {phase === "answering" && <p className="mt-1 hidden md:block text-[11px] text-slate-400">{lang === "IT" ? "Invio per continuare · Shift+Invio per andare a capo" : "Enter to continue · Shift+Enter for a new line"}</p>}

                {phase === "answering" && EXAMPLES[STEPS[idx].key] && (
                  <details className="mt-3 group rounded-2xl border border-slate-200 bg-[#f8f9fc] text-left" data-testid="flow-example">
                    <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3 text-sm font-semibold text-[#0b0f1a]" data-testid="flow-example-toggle">
                      <span className="inline-flex items-center gap-2"><Lightbulb size={15} className="text-[#7c3aed]" /> {t("flow.example_title")}</span>
                      <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-4 pb-4">
                      <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">{t("flow.example_label")}</p>
                      <p className="mt-1.5 text-sm text-slate-700 leading-relaxed" data-testid="flow-example-text">{EXAMPLES[STEPS[idx].key][lang] || EXAMPLES[STEPS[idx].key].EN}</p>
                      <p className="mt-2 text-[11px] text-slate-400">{t("flow.example_note")}</p>
                    </div>
                  </details>
                )}
                {phase === "answering" && (
                  <div className="mt-4 flex items-center gap-3 lg:justify-center min-w-0">
                    {idx > 0 && (
                      <Button data-testid="flow-back-btn" variant="ghost" onClick={goBack} className="rounded-full shrink-0 px-3"><ArrowLeft size={16} className="mr-1" /> {t("flow.back")}</Button>
                    )}
                    <Button data-testid="flow-submit-btn" onClick={submit} disabled={loading || !answer.trim()} className="cpenz-pill bg-[#7c3aed] hover:bg-[#6d28d9] text-white px-5 h-11 flex-1 sm:flex-none min-w-0 overflow-hidden">
                      {loading ? <><Loader2 size={16} className="mr-2 animate-spin shrink-0" /> <span className="truncate">{t("flow.analyzing")}</span></> : <>{t("flow.next")} <ArrowRight size={16} className="ml-1" /></>}
                    </Button>
                  </div>
                )}

                {live && loading && <span ref={tailRef} aria-hidden="true" className="block h-px" />}
                {live && loading && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-3xl border border-slate-200 bg-[#f4f6fa] p-7 text-left" data-testid="flow-result-live" aria-live="polite">
                    <div className="flex items-center gap-2">
                      <CPENZIcon size={26} />
                      <span className="text-xs font-semibold uppercase tracking-widest text-slate-400"><Lc text={t("flow.cpenz_says")} /></span>
                      <span className="ml-1 inline-block h-3 w-[2px] bg-[#7c3aed] animate-pulse" />
                    </div>
                    {live.title ? <h3 className="mt-4 text-2xl font-bold tracking-tight text-[#0b0f1a]">{live.title}</h3> : <div className="mt-4 h-7 w-2/3 rounded-md bg-slate-200/70 animate-pulse" />}
                    {live.summary ? <p className="mt-2 text-slate-600 text-lg leading-relaxed">{live.summary}</p> : <div className="mt-3 h-4 w-full rounded bg-slate-200/60 animate-pulse" />}
                    {live.momentum && <p className="mt-3 text-sm font-semibold text-[#7c3aed]">{live.momentum}</p>}
                    {live.points.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {live.points.map((p, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Sparkles size={14} className="text-[#7c3aed] mt-1 shrink-0" /><span>{p}</span></li>)}
                      </ul>
                    )}
                  </motion.div>
                )}
                {phase === "reviewing" && result && (
                  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-3xl border border-slate-200 bg-[#f4f6fa] p-7 text-left" data-testid="flow-result">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <CPENZIcon size={26} />
                        <span className="text-xs font-semibold uppercase tracking-widest text-slate-400"><Lc text={t("flow.cpenz_says")} /></span>
                      </div>
                      <ConfidenceBadge level={result.confidence} />
                    </div>
                    <h3 className="mt-4 text-2xl font-bold tracking-tight text-[#0b0f1a]">{result.title}</h3>
                    <p className="mt-2 text-slate-600 text-lg leading-relaxed">{result.summary}</p>
                    {result.momentum && <p className="mt-3 text-sm font-semibold text-[#7c3aed]">{result.momentum}</p>}
                    {STEPS[idx]?.key === "problem" && result.score_now != null && project?.snapshot?.first_score != null && (() => {
                      const from = project.snapshot.first_score, to = Number(result.score_now), promised = project.snapshot.next_uplift_pct;
                      const real = from > 0 ? Math.round(((to - from) / from) * 100) : 0;
                      return (
                        <div className="mt-5 rounded-2xl bg-[#0b0f1a] text-white p-5" data-testid="flow-promise">
                          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#c4b5fd]">{t("flow.promise_title")}</p>
                          <div className="mt-2 flex items-end gap-3 flex-wrap">
                            <span className="text-3xl font-bold tabular-nums" data-testid="flow-promise-real">{real >= 0 ? "+" : ""}{real}%</span>
                            <span className="text-sm text-white/70 pb-1">{from} → {to} · {t("flow.promise_promised")} +{promised}%</span>
                          </div>
                          <p className="mt-2 text-xs text-white/70">{real >= (promised || 0) ? t("flow.promise_beat") : real > 0 ? t("flow.promise_partial") : t("flow.promise_flat")}</p>
                        </div>
                      );
                    })()}
                    {result.points?.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {result.points.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Sparkles size={14} className="text-[#7c3aed] mt-1 shrink-0" /><span>{p}</span></li>
                        ))}
                      </ul>
                    )}
                    {result.uncertainty && (
                      <div className="mt-5 rounded-2xl bg-white border border-slate-200 p-4">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-[#f97316]">{t("flow.biggest_question")}</span>
                        <p className="mt-1 text-[#0b0f1a] font-medium">{result.uncertainty}</p>
                      </div>
                    )}
                    <div className="mt-6 flex flex-wrap items-center gap-3 lg:justify-center" ref={tailRef}>
                      {idx > 0 && <Button variant="ghost" onClick={goBack} className="rounded-full"><ArrowLeft size={16} className="mr-1" /> {t("flow.back")}</Button>}
                      <Button data-testid="flow-edit-btn" variant="outline" onClick={editAnswer} className="rounded-full border-slate-300"><Pencil size={14} className="mr-1.5" /> {lang === "IT" ? "Modifica risposta" : "Edit answer"}</Button>
                      <Button data-testid="flow-continue-btn" onClick={goNext} className="cpenz-pill bg-[#7c3aed] hover:bg-[#6d28d9] text-white px-6 h-11">
                        {t("flow.next")} <ArrowRight size={16} className="ml-1" />
                      </Button>
                    </div>
                    <p className="mt-2 hidden md:block text-center text-[11px] text-slate-400">{lang === "IT" ? "Premi Invio per continuare" : "Press Enter to continue"}</p>
                  </motion.div>
                )}
              </motion.div>
            )}

            {phase === "upsell" && (
              <motion.div key="upsell" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-xl mx-auto py-6" data-testid="flow-upsell">
                <div className="flex justify-center"><CPENZIcon size={120} /></div>
                <h1 className="mt-6 cpenz-display text-3xl sm:text-4xl text-[#0b0f1a]">{t("flow.direction_title")}</h1>
                <p className="mt-3 text-lg text-slate-500">{t("flow.direction_body")}</p>
                <MarketTeaser projectId={project?.id} onUnlock={startCheckout} />
                <div className="mt-6 rounded-3xl border border-slate-200 p-8 bg-white text-left">
                  <span className="cpenz-overline">{t("flow.unlock_title")}</span>
                  <div className="mt-3 flex items-end gap-2">
                    <span className="text-slate-500 text-sm mb-1.5">{t("pricing.from")}</span>
                    <span className="text-5xl font-bold text-[#0b0f1a]">{t("pricing.price")}</span>
                    <span className="text-slate-400 mb-1.5">{t("pricing.period")}</span>
                  </div>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                    {t("get.items").slice(0, 8).map((g, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-[#7c3aed] mt-0.5 shrink-0" /><span>{g}</span></li>
                    ))}
                  </ul>
                  <Button data-testid="flow-unlock-btn" onClick={startCheckout} className="mt-7 w-full cpenz-pill bg-[#7c3aed] hover:bg-[#6d28d9] text-white h-12">
                    {t("flow.unlock_cta")}
                  </Button>
                  {!user && (
                    <button onClick={() => authDialog.openAuth()} className="mt-3 w-full text-sm text-slate-400 hover:text-[#7c3aed]">
                      {lang === "IT" ? "Crea un account per salvare il progetto" : "Create an account to save your project"}
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {phase === "final" && (
              <motion.div key="final" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-xl mx-auto py-10" data-testid="flow-final">
                <div className="flex justify-center"><CPENZIcon size={120} /></div>
                <h1 className="mt-6 cpenz-display text-3xl sm:text-4xl text-[#0b0f1a]">{lang === "IT" ? "Hai risposto a tutto." : "You've answered everything."}</h1>
                <p className="mt-3 text-lg text-slate-500">{lang === "IT" ? "Ora cpenz.io sintetizza tutto in un punteggio e una prossima mossa." : "Now cpenz.io synthesizes everything into a score and a next move."}</p>
                <Button data-testid="flow-finalize-btn" onClick={finalize} className="mt-8 cpenz-pill bg-[#7c3aed] hover:bg-[#6d28d9] text-white h-12 px-8">
                  {t("flow.finalize")} <ArrowRight size={16} className="ml-1" />
                </Button>
              </motion.div>
            )}

            {phase === "finalizing" && (
              <motion.div key="finalizing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-24" data-testid="flow-finalizing">
                <div className="flex justify-center"><CPENZLoader size={90} /></div>
                <p className="mt-6 text-lg font-semibold text-[#0b0f1a]">{t("flow.finalizing")}</p>
                <Loader2 className="mx-auto mt-4 animate-spin text-[#7c3aed]" />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
